import { PAGE_TYPE } from '@amzn/journeys-common';
import {
  type CampaignConfigProviderCampaignFragment,
  CustomerOptionsConfigEnum,
  PageTypeEnum,
  type ScopedSlotOption,
  type ScopedVariantSlotOption,
} from 'generated/graphql';
import type { PlacementsSectionConfig, SlotOptionsPerPlatformMap } from '../../campaign-configs/types';
import { detailPageCustomerOptions, gatewayCustomerOptions } from '../customerOptions.utils';
import type { PLACEMENT_COMPONENTS } from '../placement-component-schema-utils';
import { cultivatorDesktopSlots, cultivatorMobileSlots, gatewaySlots } from '../placements-slots-schema-utils';

const PAGE_TYPE_MAP: Record<PageTypeEnum, PAGE_TYPE> = {
  [PageTypeEnum.Browse]: PAGE_TYPE.BROWSE,
  [PageTypeEnum.Detail]: PAGE_TYPE.DETAIL,
  [PageTypeEnum.Detailaw]: PAGE_TYPE.DETAILAW,
  [PageTypeEnum.Landingaw]: PAGE_TYPE.LANDINGAW,
  [PageTypeEnum.Metis]: PAGE_TYPE.METIS,
  [PageTypeEnum.Monera]: PAGE_TYPE.MONERA,
  [PageTypeEnum.Personalizationpage]: PAGE_TYPE.PERSONALIZATIONPAGE,
  [PageTypeEnum.Search]: PAGE_TYPE.SEARCH,
  [PageTypeEnum.Searchaw]: PAGE_TYPE.SEARCHAW,
  [PageTypeEnum.Gateway]: PAGE_TYPE.GATEWAY,
  [PageTypeEnum.Events]: PAGE_TYPE.EVENTS,
};

export const buildPlacementsConfig = (config: CampaignConfigProviderCampaignFragment): PlacementsSectionConfig => {
  const slotOptions = getSlotOptions(config);
  const placementComponents = config.placementsSection.placementComponents.reduce(
    (acc, val) => ({
      // biome-ignore lint/performance/noAccumulatingSpread: legacy code
      ...acc,
      [val.component]: val.enabled,
    }),
    <Record<PLACEMENT_COMPONENTS, boolean>>{},
  );
  const customerOptions =
    config.placementsSection.customerOptions === CustomerOptionsConfigEnum.DetailPage ? detailPageCustomerOptions : gatewayCustomerOptions;
  const initialPlacementValues = {
    ...config.placementsSection.initialPlacementValues,
    pageType: config.placementsSection.initialPlacementValues?.pageType
      ? PAGE_TYPE_MAP[config.placementsSection.initialPlacementValues?.pageType]
      : undefined,
    schedulingDefault: config.placementsSection.initialPlacementValues?.schedulingDefault ?? undefined,
  };

  return {
    customerOptions,
    initialPlacementValues,
    placementComponents,
    placementDefaultsEnabled: config.placementsSection.placementDefaultsEnabled,
    slotOptions,
  };
};

const slotKeywordMap = <const>{
  cultivatorDesktopSlots,
  cultivatorMobileSlots,
  ...gatewaySlots,
};

const getSlotsArr = (slots: string[]): string[] => {
  const expandedSlots = slots.flatMap((slot) => {
    if (slot in slotKeywordMap) {
      return slotKeywordMap[slot as keyof typeof slotKeywordMap];
    }
    return [
      slot,
    ];
  });
  return [
    ...new Set(expandedSlots),
  ];
};

function scopedSlotOptionsToMap(input: Array<ScopedSlotOption | ScopedVariantSlotOption>) {
  return input.reduce(
    (acc, val) => ({
      // biome-ignore lint/performance/noAccumulatingSpread: legacy code
      ...acc,
      [val.scope]: val.variantSlots.reduce(
        (acc1, val1) => ({
          // biome-ignore lint/performance/noAccumulatingSpread: legacy code
          ...acc1,
          [val1.variant]: getSlotsArr(val1.slots),
        }),
        {},
      ),
    }),
    <SlotOptionsPerPlatformMap>{},
  );
}

function getSlotOptions(config: CampaignConfigProviderCampaignFragment) {
  const slotOptionsStandard =
    config.placementsSection.slotOptions.__typename === 'StandardSlotOptions'
      ? scopedSlotOptionsToMap(config.placementsSection.slotOptions.standard)
      : undefined;
  const slotOptionsVariant =
    config.placementsSection.slotOptions.__typename === 'VariantSlotOptions'
      ? scopedSlotOptionsToMap(config.placementsSection.slotOptions.variant)
      : undefined;

  const slotOptions = slotOptionsStandard ?? slotOptionsVariant;
  if (!slotOptions) throw new Error('Expected slot options!');
  return slotOptions;
}
